import type { RegimeResult } from '@prm/shared';
import { REGIME_LABELS } from '@prm/shared';
import { mult, pct } from '../format';
import { Panel } from './Panel';
import { FlagList } from './FlagList';

export function RegimeCard({ regime }: { regime: RegimeResult }) {
  const vol = regime.realizedVol;

  return (
    <Panel
      title="Regime & Vol"
      sub={
        <>
          VIX {regime.vix === null ? '—' : regime.vix.toFixed(2)} ·{' '}
          <span className={`badge badge--${regime.regime}`}>{REGIME_LABELS[regime.regime]}</span>
        </>
      }
      flush
    >
      <div className="verdict">{regime.verdict}</div>

      <div className="panel__body">
        <div className="kv">
          <Item k="Beta-adj net" v={mult(regime.betaAdjNet)} note={`band ${regime.band.min.toFixed(2)}–${regime.band.max.toFixed(2)}×`} bad={regime.bandBreach !== 0} />
          <Item
            k="Band breach"
            v={regime.bandBreach === 0 ? 'inside' : mult(regime.bandBreach)}
            note={regime.bandBreach > 0 ? 'over the top of band' : regime.bandBreach < 0 ? 'under the band' : 'no action'}
            bad={regime.bandBreach > 0}
          />
          <Item
            k="Suggested change"
            v={regime.bandBreach === 0 ? '—' : pct(Math.abs(regime.suggestedExposureChange))}
            note={regime.suggestedExposureChange > 0 ? 'reduce to midpoint' : regime.suggestedExposureChange < 0 ? 'room to add' : ''}
            bad={regime.suggestedExposureChange > 0 && regime.bandBreach > 0}
          />
          <Item k="Realized vol 20d" v={pct(vol.daily20, 2)} note="daily" />
          <Item k="Realized vol 60d" v={pct(vol.daily60, 2)} note="daily" />
          <Item k="Annualized" v={pct(vol.annualized, 1)} note={`${vol.observations} obs`} />
          <Item k="Vol target" v={pct(regime.volTargetDaily, 2)} note="daily" />
          <Item
            k="Exposure mult"
            v={`${mult(regime.exposureMultiplier)}${regime.exposureMultiplierCapped ? ' capped' : ''}`}
            note={
              regime.exposureMultiplierCapped
                ? `raw ${mult(regime.exposureMultiplierRaw)}, clamped to 0.25–2.00×`
                : 'target / realized'
            }
            bad={regime.exposureMultiplierCapped}
          />
        </div>
      </div>

      <FlagList flags={regime.flags} empty="Regime and vol are both inside tolerance." />
    </Panel>
  );
}

function Item({ k, v, note, bad }: { k: string; v: string; note?: string; bad?: boolean }) {
  return (
    <div className="kv__item">
      <div className="kv__k">{k}</div>
      <div className={`kv__v num ${bad ? 'down' : ''}`}>{v}</div>
      {note ? <div className="faint" style={{ fontSize: 10 }}>{note}</div> : null}
    </div>
  );
}
